import React from "react"
import { Check } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import CheckinDiffPanel, { CheckinDiffOp, type TagChange } from "./CheckinDiffPanel"
import CollapsingCardList from "./CollapsingCardList"
import type { AppliesPrecision } from "../utils/dates"
import { ownerOf } from "../utils/checkinFeatures"
import type { OverrideActor } from "../api/client"

/**
 * The chrome around a set of proposed checkin writes awaiting approval:
 * a heading, the per-write diffs, and one Approve button for the whole set.
 *
 * Both producers render through here — the NL compiler behind prepare_bulk_edit
 * and the enumerated one behind propose_checkins — so a proposal looks the same
 * however it was authored, and mixed create/update/delete sets read as one list.
 *
 * In `selectable` mode the panel also owns per-item checkboxes and a "Select
 * all" toggle, and hands the checked subset to `onApprove`. That used to be the
 * deletion panel's private implementation; it lives here because the draft the
 * backend commits is approvable item by item whatever its writes do.
 */

/** One serialized ProposedWrite, as `_serialize_proposed_write` emits it. */
export interface ProposedWriteView {
    op?: string
    /**
     * The draft's own handle for this write, when the write came from one. It is
     * the `candidate_key` with a `#2`/`#3` suffix where two writes collided on
     * one, which is why it can't be reconstructed on this side.
     */
    item_key?: string
    candidate_key?: string
    target_checkin_id?: number | null
    proposed_body?: string | null
    proposed_applies_to?: string | null
    /** The time the write stores (`"09:00:00"` or null) and its precision. */
    proposed_applies_at?: string | null
    proposed_applies_precision?: AppliesPrecision | null
    current_body?: string | null
    current_applies_to?: string | null
    current_applies_at?: string | null
    current_applies_precision?: AppliesPrecision | null
    /**
     * The checkin's feature and project rows as the write leaves them, and as
     * they were when drafted. The current side is null on a create and on a
     * draft stored before it was kept. The server names each row (`title`,
     * and a feature row's `display` value) so no second fetch is needed.
     */
    proposed_feature_values?: FeatureRowView[]
    proposed_projects?: ProjectRowView[]
    current_feature_values?: FeatureRowView[] | null
    current_projects?: ProjectRowView[] | null
    reason?: string | null
    /** True when a model wrote proposed_body during the dry run. */
    generated?: boolean
}

interface FeatureRowView {
    feature_id: number
    decimal_value?: number | null
    freeform_value?: string | null
    reference_value?: number | null
    manual_override?: string | null
    override_actor?: OverrideActor | null
    title?: string
    display?: string | null
}

interface ProjectRowView {
    project_id: number
    manual_override?: string | null
    override_actor?: OverrideActor | null
    title?: string
}

// A `delete` row is a clear or a detach, so it names nothing that stays.
const isLive = (row: { manual_override?: string | null }) => row.manual_override !== "delete"

const valueOf = (row: FeatureRowView): string | null => {
    if (row.display != null) return row.display
    const raw = row.decimal_value ?? row.freeform_value ?? row.reference_value
    return raw == null ? null : String(raw)
}

type OwnedRow = { manual_override?: string | null, override_actor?: OverrideActor | null }

// Rows the parser wrote that the write keeps but makes Genie's: setting a value
// that is already there still locks it against re-parse.
const lockedByGenie = (before: OwnedRow[], after: OwnedRow[]) =>
    before.length > 0 && before.every(r => ownerOf(r) === 'parser')
    && after.some(isLive) && after.filter(isLive).every(r => ownerOf(r) === 'assistant')

// `from` minus `to`, counting repeats, so a list that drops one of two equal
// values still shows the one it dropped.
const minus = (from: string[], to: string[]): string[] => {
    const left = [...to]
    return from.filter(v => {
        const i = left.indexOf(v)
        if (i === -1) return true
        left.splice(i, 1)
        return false
    })
}

/**
 * What a write changes about a checkin's tags: per feature, the values it
 * drops and the ones it adds, and each project it attaches or detaches. A
 * create's tags are all additions. A value that stays but becomes Genie's is
 * `locked`. Empty for a delete, and for an update whose
 * draft predates the current rows being kept — there is nothing to diff.
 */
export const tagChangesOf = (w: ProposedWriteView): TagChange[] => {
    if (w.op !== "create" && w.op !== "update") return []
    const currentFeatures = w.op === "create" ? [] : w.current_feature_values
    const currentProjects = w.op === "create" ? [] : w.current_projects
    if (currentFeatures == null) return []

    const changes: TagChange[] = []
    const proposedFeatures = w.proposed_feature_values ?? []
    const featureIds = [...new Set([...proposedFeatures, ...currentFeatures].map(r => r.feature_id))]
    for (const id of featureIds) {
        const before = currentFeatures.filter(r => r.feature_id === id)
        const after = proposedFeatures.filter(r => r.feature_id === id)
        const values = (rows: FeatureRowView[]) =>
            rows.filter(isLive).map(valueOf).filter((v): v is string => v != null)
        const removed = minus(values(before), values(after))
        const added = minus(values(after), values(before))
        const title = after[0]?.title ?? before[0]?.title ?? `#${id}`
        if (removed.length || added.length) {
            changes.push({ key: `feature:${id}`, title, removed, added })
        } else if (lockedByGenie(before, after)) {
            changes.push({ key: `feature:${id}`, title, removed: [], added: [], locked: values(after) })
        }
    }

    const attached = (rows: ProjectRowView[]) => rows.filter(isLive).map(r => r.project_id)
    const before = attached(currentProjects ?? [])
    const after = attached(w.proposed_projects ?? [])
    const titleOf = (id: number) =>
        [...(w.proposed_projects ?? []), ...(currentProjects ?? [])].find(p => p.project_id === id)?.title
        ?? `#${id}`
    for (const id of after.filter(id => !before.includes(id))) {
        changes.push({ key: `project:${id}`, title: titleOf(id), project: "added" })
    }
    for (const id of before.filter(id => !after.includes(id))) {
        changes.push({ key: `project:${id}`, title: titleOf(id), project: "removed" })
    }
    const rowsOf = (rows: ProjectRowView[] | null | undefined, id: number) =>
        (rows ?? []).filter(p => p.project_id === id)
    for (const id of after.filter(id => before.includes(id))) {
        if (lockedByGenie(rowsOf(currentProjects, id), rowsOf(w.proposed_projects, id))) {
            changes.push({ key: `project:${id}`, title: titleOf(id), project: "locked" })
        }
    }
    return changes
}

/**
 * Pick the verb from the writes themselves. A set that's all one op can name it;
 * a mixed set falls back to the generic phrasing rather than picking a winner.
 */
export const headingForWrites = (writes: ProposedWriteView[], total: number): string => {
    const ops = new Set(writes.map(w => w.op))
    const singleOp = ops.size === 1 ? [...ops][0] : null
    const one = total === 1
    if (singleOp === "create") return `Would you like to create ${one ? 'this' : 'these'}?`
    if (singleOp === "delete") return `Would you like to delete ${one ? 'this' : 'these'}?`
    if (singleOp === "update") return `Would you like to apply ${one ? 'this update' : 'these updates'}?`
    return `Would you like to apply ${one ? 'this change' : 'these changes'}?`
}

interface ProposalReviewPanelProps {
    writes: ProposedWriteView[]
    formatDate: (dateString: string, includeYearIfDifferent?: boolean) => string
    /**
     * Approve the proposal. In `selectable` mode it receives the keys of the
     * writes still checked (see `keyForWrite`), in the proposal's own order —
     * which is the order the backend commits and journals them in, whatever
     * order the reviewer clicked. Callers that approve the whole set are called
     * with no argument at all, so a zero-arity handler keeps behaving exactly as
     * it did.
     */
    onApprove: (selectedKeys?: string[]) => void
    showApprove: boolean
    /**
     * Offer a checkbox per write and a "Select all" toggle, all checked to start.
     * Only takes effect while `showApprove` is true — a proposal that's no longer
     * the last word isn't selectable either.
     */
    selectable?: boolean
    /** Total writes in the proposal, when `writes` is only a preview slice. */
    totalWrites?: number
    /** Show only this many writes until the user expands. Omit to show them all. */
    initialLimit?: number
    /**
     * Render one write's card. Defaults to the diff panel. Supplied by callers
     * that already have a richer card for their writes — the deletion proposal
     * shows each checkin as it appears on the timeline, chips and all — while
     * still letting the panel own the selection chrome around it.
     */
    renderWrite?: (write: ProposedWriteView, index: number) => React.ReactNode
    /** Rendered between the heading and the list — warnings, truncation notes. */
    beforeList?: React.ReactNode
    /** Rendered after the Approve button — progress placeholders, debug details. */
    children?: React.ReactNode
}

const isDiffOp = (op: string | undefined): op is CheckinDiffOp =>
    op === "create" || op === "update" || op === "delete"

/**
 * What a selected item is called when it's reported back. The backend addresses
 * a draft's writes by `item_key` and refuses one it doesn't recognize, so a
 * write that carries its draft key reports that key verbatim. The fallbacks are
 * for writes assembled on this side — the deletion proposal's, whose
 * `candidate_key` is the checkin id its caller then reads back — and, last, a
 * rendering identity for a write that arrived with no handle at all.
 */
const keyForWrite = (w: ProposedWriteView, i: number): string =>
    w.item_key ?? w.candidate_key ?? String(i)

const ProposalReviewPanel: React.FC<ProposalReviewPanelProps> = ({
    writes,
    formatDate,
    onApprove,
    showApprove,
    selectable = false,
    totalWrites,
    initialLimit,
    renderWrite,
    beforeList,
    children,
}) => {
    // One open peek across the whole proposal — the list owns it, so opening a
    // second write's body closes the first rather than stacking modals.
    const [openWriteIndex, setOpenWriteIndex] = React.useState<number | null>(null)

    // Held as the *unchecked* keys rather than the checked ones, so "everything
    // is approved" is the empty state and a proposal that re-renders with
    // different writes starts out fully checked again without an effect to
    // resynchronize it.
    const [unchecked, setUnchecked] = React.useState<Set<string>>(() => new Set())

    const selecting = selectable && showApprove
    const keys = writes.map(keyForWrite)
    const selectedKeys = selecting ? keys.filter(k => !unchecked.has(k)) : keys
    const allSelected = selectedKeys.length === keys.length

    const toggleKey = (key: string) => setUnchecked(prev => {
        const next = new Set(prev)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
    })

    const toggleAll = () => setUnchecked(allSelected ? new Set(keys) : new Set())

    const approve = () => (selecting ? onApprove(selectedKeys) : onApprove())

    const defaultRenderWrite = (w: ProposedWriteView, i: number) => {
        if (isDiffOp(w.op)) {
            return (
                <CheckinDiffPanel
                    op={w.op}
                    onPeek={() => setOpenWriteIndex(i)}
                    peekOpen={openWriteIndex === i}
                    onPeekClose={() => setOpenWriteIndex(null)}
                    currentBody={w.current_body ?? undefined}
                    currentAppliesTo={w.current_applies_to ?? undefined}
                    currentAppliesAt={w.current_applies_at}
                    currentAppliesPrecision={w.current_applies_precision}
                    proposedBody={w.proposed_body ?? undefined}
                    proposedAppliesTo={w.proposed_applies_to ?? undefined}
                    proposedAppliesAt={w.proposed_applies_at}
                    proposedAppliesPrecision={w.proposed_applies_precision}
                    tagChanges={tagChangesOf(w)}
                    fallbackLabel={w.target_checkin_id ? `checkin #${w.target_checkin_id}` : w.candidate_key}
                    reason={w.reason ?? undefined}
                    generated={w.generated}
                    formatDate={formatDate}
                />
            )
        }
        return (
            <div className="sm:border-l-4 pl-3 sm:pl-4 py-3 sm:border-dashed sm:border-gray-400 text-sm text-gray-700">
                {w.op} {w.candidate_key}
            </div>
        )
    }

    const renderRow = (w: ProposedWriteView, i: number) => {
        const card = (renderWrite ?? defaultRenderWrite)(w, i)
        if (!selecting) return <React.Fragment key={i}>{card}</React.Fragment>
        return (
            <div key={i} className="flex items-start gap-3">
                <motion.input
                    type="checkbox"
                    aria-label={`Select item ${i + 1}`}
                    checked={!unchecked.has(keys[i])}
                    onChange={() => toggleKey(keys[i])}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    className="w-4 h-4 mt-2 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">{card}</div>
            </div>
        )
    }

    return (
        <div className="mb-4">
            <div className="text-gray-600 italic mb-4">
                {headingForWrites(writes, totalWrites ?? writes.length)}
            </div>

            {beforeList}

            {selecting && writes.length > 1 && (
                <div className="flex items-center gap-2 mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <motion.input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                            className="w-4 h-4"
                        />
                        <span className="text-gray-600">Select all</span>
                    </label>
                    {/* The count only shows once a subset is checked, and it's the
                        only cue left when the unchecked write sits below the
                        collapse threshold. */}
                    <AnimatePresence>
                        {!allSelected && (
                            <motion.span
                                initial={{ opacity: 0, scale: 0.6 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.6 }}
                                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                style={{ transformOrigin: 'left center' }}
                                className="text-xs text-gray-600 bg-gray-100 rounded-full px-2 py-0.5"
                            >
                                {selectedKeys.length} of {keys.length} selected
                            </motion.span>
                        )}
                    </AnimatePresence>
                </div>
            )}

            <CollapsingCardList
                items={writes}
                initialLimit={initialLimit}
                className="space-y-4 mb-4"
                toggleClassName="text-sm text-amber-800 hover:text-amber-900 cursor-pointer mb-3"
                renderItem={renderRow}
            />

            {showApprove && (
                <div className="flex gap-2">
                    <button
                        onClick={approve}
                        disabled={selecting && selectedKeys.length === 0}
                        className="bg-action text-plum px-4 py-2 rounded hover:bg-action-dark flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Check className="w-4 h-4" />
                        Approve
                    </button>
                </div>
            )}

            {children}
        </div>
    )
}

export default ProposalReviewPanel
